// @ts-check
// Editor de blocos (spec/email.md seção 19.2): no computador, paleta e "Estilo" à esquerda (260 px), o e-mail no meio
// (600 px) e as propriedades à direita (300 px); entre 769 e 1.099 px, a paleta, o estilo e as propriedades dividem um
// painel com abas. Arrastar da paleta e pelas alças (HTML5) e, sem arrastar, a barra do item selecionado e o "+" entre
// os blocos; cada gesto é uma operação pura do documento (o desfazer vem disso). No celular, a lista dos blocos com a
// folha inferior de propriedades. Anúncios em aria-live.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  duplicateBlock, duplicateSection, findBlock, findBlockPosition, insertBlock, insertSection, moveBlock, moveSection, removeBlock, removeSection,
  setSectionLayout, updateBlock, updateColumn, updateEmailStyle, updateSection,
} from '../../../../../shared/email-design/document-operations.js';
import { Dropdown, DropdownMenuItems, DropdownMenuSectionLabel, MenuItem } from '../../../ui/dropdown.js';
import { Icon } from '../../../ui/icon.js';
import { Modal } from '../../../ui/modal.js';
import { TabList } from '../../../ui/tab-list.js';
import { useIsMobile, useMediaQuery } from '../../../ui/hooks.js';
import { BlockCanvas } from './block-canvas.js';
import { PropertiesPanel, StylePanel } from './block-properties.js';
import { BlockMobileList } from './block-mobile-list.js';
import {
  adjacentBlockTarget, BLOCK_PALETTE_ITEMS, blockPaletteKey, createBlockFromPalette, findPaletteItem, groupProblemsByBlock, PALETTE_ITEMS, readingOrder,
  SECTION_PALETTE_ITEMS,
} from './block-catalog.js';
import { ImageLibraryDialog } from '../image-library-dialog.js';
import { rememberEmailAssetUrls } from '../local-preview.js';
import { t } from '../../../i18n/index.js';

/**
 * @typedef {import('../../../../../shared/email-design/email-document.js').EmailDocument} EmailDocument
 * @typedef {import('../../../../../shared/email-design/email-document.js').EmailBlock} EmailBlock
 * @typedef {import('./block-canvas.js').EditorSelection} EditorSelection
 * @typedef {import('./block-canvas.js').DropTarget} DropTarget
 * @typedef {import('./block-canvas.js').DragPayload} DragPayload
 * @typedef {import('./block-canvas.js').CanvasEditor} CanvasEditor
 * @typedef {import('./block-catalog.js').CompileProblem} CompileProblem
 */

const DRAG_MIME_TYPE = 'application/x-appcrm-email-block';
const WIDE_MEDIA_QUERY = '(min-width: 1100px)';

/** @returns {string} */
const createId = () => crypto.randomUUID();

/**
 * Paleta: peças arrastáveis; clicar insere depois do item selecionado (ou no fim).
 * @param {{ onDragStart: (payload: DragPayload, event: DragEvent) => void, onDragEnd: () => void, onInsert: (key: string) => void, disabled: boolean }} props
 */
function BlockPalette({ onDragStart, onDragEnd, onInsert, disabled }) {
  /** @param {typeof PALETTE_ITEMS[number]} item */
  const tile = (item) => html`<button
    type="button"
    class="email-palette-item"
    draggable=${disabled ? 'false' : 'true'}
    disabled=${disabled}
    data-kind=${item.kind}
    aria-label=${t('emailDesign.paletteInsert', { item: t(`emailDesign.palette.${item.key}`) })}
    onDragStart=${(/** @type {DragEvent} */ event) => onDragStart({ kind: 'PALETTE', key: item.key, itemKind: item.kind }, event)}
    onDragEnd=${onDragEnd}
    onClick=${() => onInsert(item.key)}
  >
    <${Icon} name=${item.icon} size=${18} />
    <span>${t(`emailDesign.palette.${item.key}`)}</span>
  </button>`;
  return html`<div class="email-palette">
    <p class="email-palette-hint">${t('emailDesign.paletteHint')}</p>
    <div class="email-palette-group" role="group" aria-label=${t('emailDesign.palette.sections')}>${SECTION_PALETTE_ITEMS.map(tile)}</div>
    <div class="email-palette-group" role="group" aria-label=${t('emailDesign.palette.blocks')}>${BLOCK_PALETTE_ITEMS.map(tile)}</div>
  </div>`;
}

/**
 * @param {{ document: EmailDocument, onChange: (document: EmailDocument, options?: { coalesceKey?: string }) => void, readOnly?: boolean,
 *   brandColor: string, logoUrl: string | null, baseUrl: string, catalog: import('../../../data/email-templates.js').EmailVariableCatalog | null,
 *   problems: { errors: CompileProblem[], warnings: CompileProblem[] }, selection: EditorSelection,
 *   onSelectionChange: (selection: EditorSelection) => void }} props
 */
export function BlockEditor({ document: emailDocument, onChange, readOnly = false, brandColor, logoUrl, baseUrl, catalog, problems, selection, onSelectionChange }) {
  const isWide = useMediaQuery(WIDE_MEDIA_QUERY);
  const isMobile = useIsMobile();
  const [leftTab, setLeftTab] = useState(/** @type {'blocks' | 'style'} */ ('blocks'));
  const [sideTab, setSideTab] = useState(/** @type {'blocks' | 'style' | 'properties'} */ ('blocks'));
  const [dragging, setDragging] = useState(/** @type {DragPayload | null} */ (null));
  const [activeDrop, setActiveDrop] = useState(/** @type {string | null} */ (null));
  const [insertMenu, setInsertMenu] = useState(/** @type {{ anchor: HTMLElement, target: DropTarget } | null} */ (null));
  const insertAnchorRef = useRef(/** @type {HTMLElement | null} */ (null));
  const [pendingDelete, setPendingDelete] = useState(/** @type {{ kind: 'BLOCK' | 'SECTION', id: string } | null} */ (null));
  const [imageLibraryBlockId, setImageLibraryBlockId] = useState(/** @type {string | null} */ (null));
  const [announcement, setAnnouncement] = useState('');
  const documentRef = useRef(emailDocument);
  documentRef.current = emailDocument;

  const order = readingOrder(emailDocument);
  const problemsByBlock = groupProblemsByBlock(problems);

  /** @param {EmailDocument} next @param {string} [coalesceKey] */
  const apply = (next, coalesceKey) => {
    if (next !== documentRef.current) onChange(next, coalesceKey ? { coalesceKey } : undefined);
  };
  /** @param {string} message */
  const announce = (message) => {
    // o mesmo texto duas vezes seguidas também precisa ser lido
    setAnnouncement('');
    requestAnimationFrame(() => setAnnouncement(message));
  };
  /** @param {string} blockId */
  const focusBlock = (blockId) => requestAnimationFrame(() => {
    /** @type {HTMLElement | null} */ (document.querySelector(`[data-block-id="${CSS.escape(blockId)}"]`))?.focus({ preventScroll: false });
  });
  /** @param {EmailBlock} block */
  const blockLabel = (block) => t(`emailDesign.palette.${blockPaletteKey(block)}`);

  // seleção que deixou de existir (desfazer, exclusão) some
  useEffect(() => {
    if (!selection) return;
    const exists = selection.kind === 'BLOCK'
      ? Boolean(findBlock(emailDocument, selection.id))
      : selection.kind === 'SECTION'
        ? Boolean(emailDocument.sections?.some((section) => section.id === selection.id))
        : Boolean(emailDocument.sections?.find((section) => section.id === selection.sectionId)?.columns[selection.columnIndex]);
    if (!exists) onSelectionChange(null);
  }, [emailDocument, selection]);

  useEffect(() => {
    if (selection && !isWide) setSideTab('properties');
  }, [selection?.kind, selection && 'id' in selection ? selection.id : null]);

  /**
   * Insere uma peça da paleta no destino.
   * @param {string} key @param {DropTarget} target
   */
  const insertPaletteItem = (key, target) => {
    const item = findPaletteItem(key);
    if (!item || readOnly) return;
    const current = documentRef.current;
    if (item.kind === 'SECTION') {
      const index = target.kind === 'SECTION' ? target.index : (current.sections ?? []).findIndex((section) => section.id === target.target.sectionId) + 1;
      const next = insertSection(current, index, item.layout ?? '100', createId);
      apply(next);
      const inserted = next.sections?.[index];
      if (inserted) onSelectionChange({ kind: 'SECTION', id: inserted.id });
      announce(t('emailDesign.announcements.sectionInserted', { position: index + 1 }));
      return;
    }
    const block = createBlockFromPalette(item.key, createId);
    if (!block) return;
    const next = target.kind === 'BLOCK' ? insertBlock(current, target.target, block) : insertSection(current, target.index, '100', createId, [block]);
    apply(next);
    onSelectionChange({ kind: 'BLOCK', id: block.id });
    announce(t('emailDesign.announcements.inserted', { type: blockLabel(block), position: readingOrder(next).get(block.id) ?? 1 }));
    focusBlock(block.id);
  };

  /** Clique numa peça da paleta: depois do bloco ou da seção selecionada, senão no fim. @param {string} key */
  const insertFromPalette = (key) => {
    const current = documentRef.current;
    const sections = current.sections ?? [];
    const item = findPaletteItem(key);
    /** @type {DropTarget} */
    let target = { kind: 'SECTION', index: sections.length };
    if (selection?.kind === 'BLOCK') {
      const position = findBlockPosition(current, selection.id);
      if (position) target = item?.kind === 'SECTION' ? { kind: 'SECTION', index: sections.findIndex((section) => section.id === position.sectionId) + 1 } : { kind: 'BLOCK', target: { ...position, index: position.index + 1 } };
    } else if (selection?.kind === 'SECTION') {
      target = { kind: 'SECTION', index: sections.findIndex((section) => section.id === selection.id) + 1 };
    } else if (selection?.kind === 'COLUMN' && item?.kind === 'BLOCK') {
      const column = sections.find((section) => section.id === selection.sectionId)?.columns[selection.columnIndex];
      target = { kind: 'BLOCK', target: { sectionId: selection.sectionId, columnIndex: selection.columnIndex, index: column?.blocks.length ?? 0 } };
    }
    insertPaletteItem(key, target);
  };

  /** @param {DropTarget} target */
  const drop = (target) => {
    const payload = dragging;
    setDragging(null);
    setActiveDrop(null);
    if (!payload || readOnly) return;
    const current = documentRef.current;
    if (payload.kind === 'PALETTE') {
      insertPaletteItem(payload.key, target);
      return;
    }
    if (payload.kind === 'SECTION') {
      if (target.kind !== 'SECTION') return;
      const next = moveSection(current, payload.id, target.index);
      apply(next);
      const position = (next.sections ?? []).findIndex((section) => section.id === payload.id) + 1;
      announce(t('emailDesign.announcements.sectionMoved', { position }));
      return;
    }
    const block = findBlock(current, payload.id);
    if (!block) return;
    const next = target.kind === 'BLOCK'
      ? moveBlock(current, payload.id, target.target)
      : insertSection(removeBlock(current, payload.id), target.index, '100', createId, [block]);
    apply(next);
    announce(t('emailDesign.announcements.moved', { type: blockLabel(block), position: readingOrder(next).get(block.id) ?? 1 }));
    focusBlock(block.id);
  };

  /** @param {DragPayload} payload @param {DragEvent} event @param {Element | null} [image] */
  const startDrag = (payload, event, image) => {
    if (readOnly || !event.dataTransfer) return;
    event.dataTransfer.effectAllowed = 'copyMove';
    event.dataTransfer.setData(DRAG_MIME_TYPE, JSON.stringify(payload));
    event.dataTransfer.setData('text/plain', '');
    if (image) event.dataTransfer.setDragImage(image, 16, 16);
    // a zona de soltar aparece no quadro seguinte: mudar o DOM durante o dragstart cancela o arraste no Chrome
    requestAnimationFrame(() => setDragging(payload));
  };
  const endDrag = () => {
    setDragging(null);
    setActiveDrop(null);
  };

  /** @param {string} blockId @param {-1 | 1} direction */
  const canMoveBlock = (blockId, direction) => {
    const position = findBlockPosition(emailDocument, blockId);
    return Boolean(position && adjacentBlockTarget(emailDocument, position, direction));
  };

  /** @param {'UP' | 'DOWN' | 'DUPLICATE' | 'DELETE'} action @param {string} blockId */
  const blockAction = (action, blockId) => {
    if (readOnly) return;
    const current = documentRef.current;
    const block = findBlock(current, blockId);
    const position = findBlockPosition(current, blockId);
    if (!block || !position) return;
    if (action === 'DELETE') {
      setPendingDelete({ kind: 'BLOCK', id: blockId });
      return;
    }
    if (action === 'DUPLICATE') {
      const next = duplicateBlock(current, blockId, createId);
      apply(next);
      const copy = next.sections?.find((section) => section.id === position.sectionId)?.columns[position.columnIndex].blocks[position.index + 1];
      if (copy) {
        onSelectionChange({ kind: 'BLOCK', id: copy.id });
        announce(t('emailDesign.announcements.duplicated', { type: blockLabel(block), position: readingOrder(next).get(copy.id) ?? 1 }));
        focusBlock(copy.id);
      }
      return;
    }
    const target = adjacentBlockTarget(current, position, action === 'UP' ? -1 : 1);
    if (!target) return;
    const next = moveBlock(current, blockId, target);
    apply(next);
    announce(t('emailDesign.announcements.moved', { type: blockLabel(block), position: readingOrder(next).get(blockId) ?? 1 }));
    focusBlock(blockId);
  };

  /** @param {'UP' | 'DOWN' | 'DUPLICATE' | 'DELETE'} action @param {string} sectionId */
  const sectionAction = (action, sectionId) => {
    if (readOnly) return;
    const current = documentRef.current;
    const sections = current.sections ?? [];
    const index = sections.findIndex((section) => section.id === sectionId);
    if (index < 0) return;
    if (action === 'DELETE') {
      setPendingDelete({ kind: 'SECTION', id: sectionId });
      return;
    }
    if (action === 'DUPLICATE') {
      const next = duplicateSection(current, sectionId, createId);
      apply(next);
      const copy = next.sections?.[index + 1];
      if (copy) onSelectionChange({ kind: 'SECTION', id: copy.id });
      announce(t('emailDesign.announcements.sectionDuplicated', { position: index + 2 }));
      return;
    }
    const targetIndex = action === 'UP' ? index - 1 : index + 2;
    if (targetIndex < 0 || targetIndex > sections.length) return;
    const next = moveSection(current, sectionId, targetIndex);
    apply(next);
    announce(t('emailDesign.announcements.sectionMoved', { position: (next.sections ?? []).findIndex((section) => section.id === sectionId) + 1 }));
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    const current = documentRef.current;
    if (pendingDelete.kind === 'BLOCK') {
      const block = findBlock(current, pendingDelete.id);
      apply(removeBlock(current, pendingDelete.id));
      if (block) announce(t('emailDesign.announcements.deleted', { type: blockLabel(block) }));
    } else {
      apply(removeSection(current, pendingDelete.id));
      announce(t('emailDesign.announcements.sectionDeleted'));
    }
    onSelectionChange(null);
    setPendingDelete(null);
  };

  /** @param {string} blockId @param {Record<string, unknown>} patch @param {string} [coalesceKey] */
  const changeBlock = (blockId, patch, coalesceKey) => apply(updateBlock(documentRef.current, blockId, patch), coalesceKey);

  /** @type {CanvasEditor} */
  const canvasEditor = {
    document: emailDocument,
    readOnly,
    brandColor,
    logoUrl,
    baseUrl,
    catalog,
    selection,
    select: onSelectionChange,
    dragging,
    startDrag,
    endDrag,
    activeDrop,
    setActiveDrop,
    drop,
    openInsertMenu: (anchor, target) => {
      insertAnchorRef.current = anchor;
      setInsertMenu({ anchor, target });
    },
    blockAction,
    canMoveBlock,
    sectionAction,
    updateBlock: changeBlock,
    openImageLibrary: (blockId) => {
      onSelectionChange({ kind: 'BLOCK', id: blockId });
      setImageLibraryBlockId(blockId);
    },
    problemsByBlock,
    readingOrder: order,
  };
  const propertiesEditor = {
    document: emailDocument,
    selection,
    readOnly,
    brandColor,
    catalog,
    updateBlock: changeBlock,
    /** @param {string} sectionId @param {Record<string, unknown>} patch @param {string} [coalesceKey] */
    updateSection: (sectionId, patch, coalesceKey) => apply(updateSection(documentRef.current, sectionId, patch), coalesceKey),
    /** @param {string} sectionId @param {any} layout */
    setSectionLayout: (sectionId, layout) => apply(setSectionLayout(documentRef.current, sectionId, layout, createId)),
    /** @param {string} sectionId @param {number} columnIndex @param {Record<string, unknown>} patch @param {string} [coalesceKey] */
    updateColumn: (sectionId, columnIndex, patch, coalesceKey) => apply(updateColumn(documentRef.current, sectionId, columnIndex, patch), coalesceKey),
    openImageLibrary: setImageLibraryBlockId,
    select: onSelectionChange,
    problemsByBlock,
  };
  const stylePanel = html`<${StylePanel} style=${emailDocument.style} readOnly=${readOnly} brandColor=${brandColor}
    onChange=${(/** @type {any} */ patch, /** @type {string | undefined} */ coalesceKey) => apply(updateEmailStyle(documentRef.current, patch), coalesceKey)} />`;
  const palette = html`<${BlockPalette} disabled=${readOnly} onDragStart=${startDrag} onDragEnd=${endDrag} onInsert=${insertFromPalette} />`;

  const insertTarget = insertMenu?.target ?? null;
  const overlays = html`
    <${Dropdown} anchorRef=${insertAnchorRef} isOpen=${Boolean(insertMenu)} onClose=${() => setInsertMenu(null)} width=${220} role="menu">
      <${DropdownMenuItems} hasMaxHeight=${true}>
        ${insertTarget?.kind === 'SECTION'
          ? html`<${DropdownMenuSectionLabel} label=${t('emailDesign.palette.sections')} />
              ${SECTION_PALETTE_ITEMS.map((item) => html`<${MenuItem} key=${item.key} icon=${item.icon} text=${t(`emailDesign.palette.${item.key}`)}
                onClick=${() => { setInsertMenu(null); if (insertTarget) insertPaletteItem(item.key, insertTarget); }} />`)}
              <${DropdownMenuSectionLabel} label=${t('emailDesign.palette.blocks')} />`
          : null}
        ${BLOCK_PALETTE_ITEMS.map((item) => html`<${MenuItem} key=${item.key} icon=${item.icon} text=${t(`emailDesign.palette.${item.key}`)}
          onClick=${() => { setInsertMenu(null); if (insertTarget) insertPaletteItem(item.key, insertTarget); }} />`)}
      <//>
    <//>
    ${pendingDelete
      ? html`<${Modal}
          title=${pendingDelete.kind === 'BLOCK' ? t('emailDesign.confirmDelete.blockTitle') : t('emailDesign.confirmDelete.sectionTitle')}
          subtitle=${pendingDelete.kind === 'BLOCK' ? t('emailDesign.confirmDelete.blockDescription') : t('emailDesign.confirmDelete.sectionDescription')}
          confirmLabel=${t('emailDesign.toolbar.delete')} confirmAccent="danger" onClose=${() => setPendingDelete(null)} onConfirm=${confirmDelete} />`
      : null}
    ${imageLibraryBlockId
      ? html`<${ImageLibraryDialog}
          onClose=${() => setImageLibraryBlockId(null)}
          onSelect=${(/** @type {import('../../../data/email-templates.js').EmailAsset} */ asset) => {
            rememberEmailAssetUrls([asset]);
            changeBlock(imageLibraryBlockId, { source: { kind: 'ASSET', assetId: asset.id, extension: asset.extension } });
            setImageLibraryBlockId(null);
          }}
        />`
      : null}
    <div class="email-visually-hidden" aria-live="polite" role="status">${announcement}</div>
  `;

  if (isMobile) {
    return html`<div class="email-block-editor" data-layout="mobile">
      <${BlockMobileList}
        canvasEditor=${canvasEditor}
        propertiesEditor=${propertiesEditor}
        stylePanel=${stylePanel}
        onAdd=${(/** @type {HTMLElement} */ anchor, /** @type {DropTarget} */ target) => canvasEditor.openInsertMenu(anchor, target)}
      />
      ${overlays}
    </div>`;
  }

  if (isWide) {
    return html`<div class="email-block-editor" data-layout="wide" data-dragging=${dragging ? 'true' : undefined}>
      ${readOnly
        ? null
        : html`<aside class="email-editor-panel email-editor-left" aria-label=${t('emailDesign.palette.title')}>
            <${TabList} tabs=${[{ id: 'blocks', title: t('emailDesign.palette.title') }, { id: 'style', title: t('emailDesign.palette.style') }]}
              activeTabId=${leftTab} onChange=${(/** @type {any} */ tab) => setLeftTab(tab)} />
            <div class="email-editor-panel-body">${leftTab === 'blocks' ? palette : stylePanel}</div>
          </aside>`}
      <div class="email-editor-canvas-area"><${BlockCanvas} editor=${canvasEditor} /></div>
      ${readOnly
        ? null
        : html`<aside class="email-editor-panel email-editor-right" aria-label=${t('emailDesign.properties.title')}>
            <div class="email-editor-panel-body"><${PropertiesPanel} editor=${propertiesEditor} /></div>
          </aside>`}
      ${overlays}
    </div>`;
  }

  return html`<div class="email-block-editor" data-layout="medium" data-dragging=${dragging ? 'true' : undefined}>
    <div class="email-editor-canvas-area"><${BlockCanvas} editor=${canvasEditor} /></div>
    ${readOnly
      ? null
      : html`<aside class="email-editor-panel email-editor-right" aria-label=${t('emailDesign.properties.title')}>
          <${TabList}
            tabs=${[
              { id: 'blocks', title: t('emailDesign.palette.title') },
              { id: 'style', title: t('emailDesign.palette.style') },
              { id: 'properties', title: t('emailDesign.properties.title') },
            ]}
            activeTabId=${sideTab}
            onChange=${(/** @type {any} */ tab) => setSideTab(tab)}
          />
          <div class="email-editor-panel-body">
            ${sideTab === 'blocks' ? palette : sideTab === 'style' ? stylePanel : html`<${PropertiesPanel} editor=${propertiesEditor} />`}
          </div>
        </aside>`}
    ${overlays}
  </div>`;
}
